import type { CustomerRevenueReportItem } from '@vy/types';
import type { Workbook, Worksheet } from 'exceljs';
import {
  expandCustomerRevenueReportItems,
  getCustomerRevenueProductName,
  getRevenueTypeLabel,
  ROW_LEVELS,
  type ExpandedCustomerRevenueReportRow,
} from '../../../customer-revenue-report-page/customer-revenue-report-components/CustomerRevenueReportTable/customer-revenue-report-row-expander';
import {
  getNumericRevenueForDate,
  getRowRecognizedTotal,
  getRowRemainingTotal,
} from '../../../customer-revenue-report-page/customer-revenue-report-components/CustomerRevenueReportTable/customer-revenue-report-calculations';

const LINEAGE_HEADERS = ['Customer', 'Product', 'Revenue Type', 'Currency'] as const;
const TOTAL_HEADERS = ['Recognized', 'Remaining'] as const;
const LINEAGE_COLUMN_COUNT = LINEAGE_HEADERS.length;
const TOTAL_COLUMN_COUNT = TOTAL_HEADERS.length;

export function buildWaterfallSheet(
  workbook: Workbook,
  searchedItems: CustomerRevenueReportItem[],
  dateHeaders: string[],
  allDateHeaders: string[],
): void {
  const worksheet = workbook.addWorksheet('Revenue Waterfall');
  const allHeaders = [...LINEAGE_HEADERS, ...dateHeaders, ...TOTAL_HEADERS];

  writeWaterfallHeader(worksheet, allHeaders);
  writeWaterfallRows(worksheet, searchedItems, dateHeaders, allDateHeaders);
  applyWaterfallSheetColumnStyles(worksheet, dateHeaders);
}

function writeWaterfallHeader(worksheet: Worksheet, allHeaders: string[]): void {
  worksheet.addRow(allHeaders);
  worksheet.getRow(1).font = { bold: true };
  worksheet.views = [{ state: 'frozen', xSplit: 3, ySplit: 1 }];
  worksheet.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: 1, column: allHeaders.length },
  };
}

function writeWaterfallRows(
  worksheet: Worksheet,
  searchedItems: CustomerRevenueReportItem[],
  dateHeaders: string[],
  allDateHeaders: string[],
): void {
  const expandedRows = expandCustomerRevenueReportItems(searchedItems);
  const revenueTypeRows = expandedRows.filter((row) => row.level === ROW_LEVELS.RevenueType);

  for (const row of revenueTypeRows) {
    const values = buildWaterfallRowValues(row, dateHeaders, allDateHeaders);

    if (isEmptyDataRow(values)) {
      continue;
    }

    const excelRow = worksheet.addRow(values);
    excelRow.commit();
  }
}

function isEmptyDataRow(values: (string | number | null)[]): boolean {
  const numericValues = values.slice(LINEAGE_COLUMN_COUNT);
  return numericValues.every((value) => value === null);
}

function buildWaterfallRowValues(
  row: ExpandedCustomerRevenueReportRow,
  dateHeaders: string[],
  allDateHeaders: string[],
): (string | number | null)[] {
  const customerName = row.customer.name;
  const productName = getCustomerRevenueProductName(row, '—');
  const revenueTypeLabel = getRevenueTypeLabel(row.revenueType);
  const currency = row.customer.currency ?? '';

  const dateValues = dateHeaders.map((header) =>
    nullIfZero(getNumericRevenueForDate(row, header, allDateHeaders)),
  );

  const recognized = nullIfZero(getRowRecognizedTotal(row, dateHeaders));
  const remaining = nullIfZero(getRowRemainingTotal(row, dateHeaders));

  return [customerName, productName, revenueTypeLabel, currency, ...dateValues, recognized, remaining];
}

function nullIfZero(value: number): number | null {
  return value === 0 ? null : value;
}

function applyWaterfallSheetColumnStyles(worksheet: Worksheet, dateHeaders: string[]): void {
  applyLineageColumnStyles(worksheet);
  applyDateColumnStyles(worksheet, dateHeaders);
  applyTotalColumnStyles(worksheet, dateHeaders.length);
  applyHeaderRowAlignment(worksheet, dateHeaders.length);
}

function applyLineageColumnStyles(worksheet: Worksheet): void {
  const widths = [28, 28, 18, 14];
  for (let index = 0; index < LINEAGE_COLUMN_COUNT; index++) {
    const column = worksheet.getColumn(index + 1);
    column.width = widths[index];
    column.alignment = { horizontal: 'left' };
  }
}

function applyDateColumnStyles(worksheet: Worksheet, dateHeaders: string[]): void {
  for (let index = 0; index < dateHeaders.length; index++) {
    const column = worksheet.getColumn(LINEAGE_COLUMN_COUNT + index + 1);
    column.width = 16;
    column.numFmt = '#,##0.00';
    column.alignment = { horizontal: 'right' };
  }
}

function applyTotalColumnStyles(worksheet: Worksheet, dateHeaderCount: number): void {
  for (let index = 0; index < TOTAL_COLUMN_COUNT; index++) {
    const column = worksheet.getColumn(LINEAGE_COLUMN_COUNT + dateHeaderCount + index + 1);
    column.width = 16;
    column.numFmt = '#,##0.00';
    column.alignment = { horizontal: 'right' };
  }
}

function applyHeaderRowAlignment(worksheet: Worksheet, dateHeaderCount: number): void {
  const totalColumns = LINEAGE_COLUMN_COUNT + dateHeaderCount + TOTAL_COLUMN_COUNT;
  for (let columnIndex = 1; columnIndex <= totalColumns; columnIndex++) {
    worksheet.getRow(1).getCell(columnIndex).alignment = { horizontal: 'left' };
  }
}
